import * as React from 'react';
import Alert from 'antd/lib/alert';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { FireOutlined, LoadingOutlined, UndoOutlined, WarningFilled } from '@ant-design/icons';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetActiveHotfixRequestsQuery } from '@/middlewares/defaultApi';
import { createHotfixRequestRoute } from '@/helpers/routeHelpers';
import { selectIsDemoInstanceOrDemoModeEnabled } from '@/selectors/selectors';
import type { RootState } from '@/reducers/reducers';
import { releaseMetaTextStyle, runwayColors } from '@/components/styles/globalStyles';
import styles from '@/components/styles/timelineStyles';
import type { SidebarColors } from '@/components/styles/themeMode';
import StatusNode from '@/components/StatusNode';
import { rollbackStripeBackground } from '@/components/sidebar/useReleaseNodeDisplay';
import { hotfixRequestStatusLabel, isActiveHotfixRequest } from './reviewHelpers';

interface Props {
    orgId: string;
    appId: string;
    sidebarColors: SidebarColors;
}

export default function HotfixRequestsSidebarGroup({ orgId, appId, sidebarColors: sc }: Props) {
    const history = useHistory();
    const location = useLocation();
    const isDemoMode = useSelector((state: RootState) => selectIsDemoInstanceOrDemoModeEnabled(state, orgId));
    const query = useGetActiveHotfixRequestsQuery({ orgId, appId }, { skip: isDemoMode });
    const requests = (query.currentData ?? []).filter(isActiveHotfixRequest);
    const selectedRequestId = matchPath<{ requestId: string }>(location.pathname, {
        path: createHotfixRequestRoute(':orgId', ':appId', ':requestId'),
        exact: true,
    })?.params.requestId;

    if (isDemoMode) return null;
    if (query.isLoading) {
        return (
            <div style={{ padding: '12px 18px', display: 'flex', justifyContent: 'center' }}>
                <Spin size="small" />
            </div>
        );
    }
    if (query.error && requests.length === 0) {
        return (
            <div style={{ padding: '8px 16px' }}>
                <Alert type="error" message="Unable to load hotfix requests" showIcon />
            </div>
        );
    }
    if (requests.length === 0) return null;

    return (
        <section
            aria-label="Active hotfix requests"
            style={{ display: 'flex', flexDirection: 'column', gap: 0, paddingBottom: 10 }}
        >
            <Text
                style={{
                    ...styles.timelineHeaderStyle,
                    textTransform: 'uppercase',
                    fontSize: 12,
                    fontWeight: 400,
                    color: sc.textSecondary,
                    letterSpacing: '0.1em',
                }}
            >
                Hotfix request
            </Text>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {requests.map((request, index) => {
                    const statusLabel =
                        request.status === 'pending' ? 'Awaiting review' : hotfixRequestStatusLabel(request.status);
                    const isRollback = request.proposal.releaseType === 'rollback';
                    const requestTitle = request.proposal.proposedVersion;
                    const isSelected = selectedRequestId === request.id;
                    return (
                        <li key={request.id}>
                            <button
                                type="button"
                                onClick={() => history.push(createHotfixRequestRoute(orgId, appId, request.id))}
                                style={{
                                    ...styles.timelineContent,
                                    width: '100%',
                                    border: 0,
                                    borderTop: `0.5px solid ${sc.border}`,
                                    borderBottom:
                                        index === requests.length - 1 ? `0.5px solid ${sc.border}` : undefined,
                                    backgroundColor: 'transparent',
                                    ...rollbackStripeBackground(
                                        isSelected ? sc.menuActiveBg : sc.menuHoverBg,
                                        isSelected ? sc.menuHoverBg : sc.bg,
                                    ),
                                    color: sc.text,
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    display: 'flex',
                                    gap: 20,
                                    alignItems: 'center',
                                }}
                            >
                                <StatusNode
                                    statusColor={runwayColors.statusYellow}
                                    outerBorderColor="transparent"
                                    outerBorderWidth={0}
                                    hideConnector
                                    size={12}
                                />
                                <span
                                    style={{
                                        minWidth: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        flexShrink: 1,
                                    }}
                                >
                                    <Text ellipsis style={{ color: sc.text }}>
                                        {requestTitle}
                                    </Text>
                                    {request.status === 'approving' ?
                                        <LoadingOutlined spin style={{ color: runwayColors.statusYellow }} />
                                    : isRollback ?
                                        <UndoOutlined style={{ color: sc.icon }} />
                                    :   <FireOutlined style={{ color: runwayColors.statusYellow }} />}
                                </span>
                                <div
                                    style={{
                                        flexGrow: 1,
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}
                                >
                                    {request.lastApprovalError && (
                                        <Tooltip title={request.lastApprovalError}>
                                            <WarningFilled style={{ color: runwayColors.statusRed }} />
                                        </Tooltip>
                                    )}
                                    <Text
                                        style={{
                                            ...releaseMetaTextStyle,
                                            fontSize: 9,
                                            letterSpacing: '0.05em',
                                            color: runwayColors.statusYellow,
                                            backgroundColor: sc.dropdownBg,
                                            borderRadius: 2,
                                            padding: '1px 4px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {statusLabel}
                                    </Text>
                                </div>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
